import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { Wallet, MembershipPermissions } from '../../../core/models';
import { NotificationService, SessionService } from '../../../core/services';
import { currencySymbol } from '../../../core/utils/currency-symbol';

import { BillingService } from '../services/billing.service';
import { WalletTransaction } from '../models/transaction.model';

/** Below this the balance card turns amber ("Low balance") so there's time to top up. */
export const LOW_BALANCE_CENTS = 500;

export type WalletStatus = 'active' | 'low' | 'empty' | 'overdrawn';


@Component({
  selector: 'app-billing',
  standalone: false,
  templateUrl: './billing.html',
  styleUrl: './billing.scss',
})
export class Billing implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  wallet: Wallet | null = null;
  recentTransactions: WalletTransaction[] = [];
  isLoading = false;
  isToppingUp = false;

  readonly membership$: Observable<MembershipPermissions | null>;
  readonly quickAmounts = [1000, 2500, 5000, 10000];
  readonly currencySymbol = currencySymbol;
  selectedAmountCents: number | null = null;
  customAmount = '';

  constructor(
    private billingService: BillingService,
    private notificationService: NotificationService,
    private sessionService: SessionService,
  ) {
    this.membership$ = this.sessionService.membership$;
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.billingService.getWallet()
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          this.notificationService.error('Failed to load your wallet. Please try again later.');
          return throwError(() => error);
        }),
        tap(({ wallet, recentTransactions }) => {
          this.wallet = wallet;
          this.recentTransactions = recentTransactions;
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  selectAmount(cents: number): void {
    this.selectedAmountCents = cents;
    this.customAmount = '';
  }

  /** View-only: the wallet request finished without a wallet, so show the error card. */
  get loadFailed(): boolean {
    return !this.isLoading && !this.wallet;
  }

  get walletStatus(): WalletStatus {
    const cents = this.wallet?.balanceCents ?? 0;
    if (cents < 0) return 'overdrawn';
    if (cents === 0) return 'empty';
    return cents < LOW_BALANCE_CENTS ? 'low' : 'active';
  }

  /** Every endpoint costs one credit per call (docs: Pricing & limits), so credits left = requests left. */
  get requestsLeft(): number {
    if (!this.wallet || this.wallet.balanceCents <= 0 || !this.wallet.pricePerCreditCents) return 0;
    return Math.floor(this.wallet.balanceCents / this.wallet.pricePerCreditCents);
  }

  get topUpAmountCents(): number | null {
    if (this.customAmount) {
      const parsed = Math.round(parseFloat(this.customAmount) * 100);
      return !parsed || parsed <= 0 ? null : parsed;
    }
    return this.selectedAmountCents;
  }

  addFunds(): void {
    const amountCents = this.topUpAmountCents;
    if (!amountCents) {
      this.notificationService.error('Enter a valid amount first.');
      return;
    }

    this.isToppingUp = true;
    this.billingService.createTopUp(amountCents)
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          const apiError = error?.error?.error;
          const message = apiError?.code === 'topup_amount_invalid'
            ? apiError.message
            : apiError?.code === 'forbidden'
              ? 'Only the organization owner can add funds.'
              : 'Could not start checkout. Please try again later.';
          this.notificationService.error(message);
          return throwError(() => error);
        }),
        tap((session: { url: string }) => {
          // Hand off to Stripe's hosted checkout page nothing is credited until the
          // checkout.session.completed webhook fires on the backend.
          window.location.href = session.url;
        }),
        finalize(() => { this.isToppingUp = false; }),
      ).subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
